"use client";

import React, { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "next/navigation";
import NextLink from "next/link";
import { useTranslations, useLocale } from "next-intl";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Header } from "@components/header";
import RoomPreview from "src/components/art/RoomPreview";
import ArtworkArButton from "src/components/art/ArtworkArButton";
import ArtworkRow from "src/components/art/ArtworkRow";
import QuickAddSheet from "src/components/art/QuickAddSheet";
import type { ArtworkCardLinks } from "src/components/art/ArtworkCard";
import { useArtwork, useArtworks, artworkImageUrl } from "src/components/art/useArtwork";
import { useArtworkOptions } from "src/components/art/useArtworkOptions";
import { useAddArtworkToCart } from "src/components/art/useAddArtworkToCart";
import { useWishlist } from "src/components/art/useWishlist";
import { useCardOffers } from "src/components/art/useCardOffers";
import ArtworkSetLink from "src/components/art/ArtworkSetLink";
import { artworkAspect, localized, type Artwork } from "src/components/art/artwork-types";
import { printableArtworks } from "src/components/art/hide-unprintable";
import { moreByArtist, similarWorks } from "src/components/art/artwork-store";
import FinishPicker from "src/components/canvas/FinishPicker";
import SizePicker from "src/components/canvas/SizePicker";
import TrustStrip from "src/components/canvas/TrustStrip";
import ReviewsMarquee from "src/components/canvas/ReviewsMarquee";
import { OptionDensityContext, STEP_COUNTER } from "src/components/canvas/OptionCard";
import { trustPointKeys } from "src/components/canvas/trust-points";
import { formatMoney } from "src/components/canvas/variant-price";
import { MobileContinueBar } from "src/components/canvas/MobileContinueBar";
import { sizeLabel } from "src/components/canvas/size-groups";

export interface ArtworkDetailProps {
  /** Where "back" and the similar works go: art or photography. */
  basePath: string;
  /** Read on the server for the metadata; saves the page a loading spinner. */
  initialArtwork?: Artwork | null;
}

/**
 * One piece: see it on a wall, choose a finish and a size, buy it.
 *
 * There is no crop tool here on purpose. The customer did not make this
 * picture and has no business reframing a Vermeer — the work is shown whole,
 * and the only decisions are what it is printed on and how big it hangs.
 *
 * Those two are asked with the canvas editor's own pickers, so a customer who
 * has bought a canvas of their own photo meets the same cards, the same size
 * tiles and the same prices here.
 */
export default function ArtworkDetail({ basePath, initialArtwork }: ArtworkDetailProps) {
  const t = useTranslations("art");
  const tEditor = useTranslations("editor");
  const te = tEditor as unknown as (key: string) => string;
  const locale = useLocale();
  const params = useParams();
  const slug = params?.slug as string | undefined;
  const localeSegment = (params?.locale as string) ?? locale;
  const root = `/${localeSegment}/${basePath}`;
  const optionsRef = useRef<HTMLHRElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"), { noSsr: true });

  const { data: artwork, isLoading, error } = useArtwork(slug, initialArtwork);
  const {
    variants,
    axes,
    printable,
    printableVariants,
    selected,
    select,
    finish,
    changeFinishTo,
    groups,
    currency,
    price,
  } = useArtworkOptions(artwork);
  const { data: siblings } = useArtworks(artwork?.kind ?? "ART");

  // One add-to-cart for the page: the main button and the quick-add sheet on
  // the rows below share it, and with it the cart drawer's way to checkout.
  const { add, adding, error: pageError, clearError } = useAddArtworkToCart(
    t("addToCartFailed"),
  );
  const [quickAdd, setQuickAdd] = useState<Artwork | null>(null);
  const { saved, toggle: toggleSaved } = useWishlist();

  const aspect = artwork ? artworkAspect(artwork) : 1;
  const orientation = aspect >= 1 ? "landscape" : "portrait";

  // Other works worth a look, from the same storefront and only ones that can
  // be bought.
  const buyableSiblings = useMemo(
    () => printableArtworks(siblings ?? [], variants),
    [siblings, variants],
  );
  const similar = useMemo(
    () => (artwork ? similarWorks(artwork, buyableSiblings) : []),
    [artwork, buyableSiblings],
  );
  const byArtist = useMemo(
    () => (artwork ? moreByArtist(artwork, buyableSiblings) : []),
    [artwork, buyableSiblings],
  );
  // A work in both rows is shown once, under the artist.
  const similarOnly = useMemo(
    () => similar.filter((a) => !byArtist.some((b) => b.id === a.id)),
    [similar, byArtist],
  );
  const offersOf = useCardOffers(buyableSiblings, variants);
  const cardProps = (a: Artwork): ArtworkCardLinks => ({
    href: `${root}/${a.slug}`,
    locale,
    ...offersOf(a),
    onQuickAdd: setQuickAdd,
    saved: saved.has(a.id),
    onToggleSaved: () => toggleSaved(a.id),
  });

  const handleAdd = useCallback(() => {
    if (artwork && selected) void add(artwork.slug, selected.variant.id);
  }, [artwork, selected, add]);

  if (isLoading) {
    return (
      <>
        <Header sticky={false} />
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !artwork) {
    return (
      <>
        <Header sticky={false} />
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Alert severity="error">{t("notFound")}</Alert>
        </Container>
      </>
    );
  }

  const title = localized(artwork.title, locale);
  const description = localized(artwork.description, locale);
  const material = selected?.variant.axisValues?.material;
  const mount = selected?.variant.axisValues?.mount;

  return (
    <>
      <Header sticky={false} />
      <Snackbar
        open={!!pageError && !quickAdd}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={clearError}>
          {pageError}
        </Alert>
      </Snackbar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Button component={NextLink} href={root} sx={{ mb: 2, pl: 0 }}>
          ← {t("backToGallery")}
        </Button>

        <Grid container spacing={{ xs: 3, md: 5 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <RoomPreview
              src={artworkImageUrl(artwork.slug, "display", artwork.updatedAt)}
              alt={`${title} — ${artwork.artist}`}
              aspect={aspect}
              widthMm={selected?.widthMm ?? 400}
              heightMm={selected?.heightMm ?? 300}
              captions={{
                sofa: t("trueScale"),
                bed: t("trueScaleBed"),
                plain: t("plainView"),
              }}
              sceneLabels={{
                sofa: t("sceneSofa"),
                bed: t("sceneBed"),
                plain: t("scenePlain"),
              }}
            />
            {/* The chosen size and finish at real size, in AR; under the
                preview, as in the povečave editor. */}
            <ArtworkArButton
              kind={basePath === "photography" ? "photography" : "art"}
              artwork={artwork}
              size={selected}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            {/* The museum tombstone: who, what, when. */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ letterSpacing: ".1em", textTransform: "uppercase" }}
            >
              {artwork.artist}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mt: 0.5 }}>
                {title}
              </Typography>
              {/* Kept in this browser, like the gallery's hearts. */}
              <IconButton
                onClick={() => toggleSaved(artwork.id)}
                aria-pressed={saved.has(artwork.id)}
                aria-label={t(saved.has(artwork.id) ? "wishlistRemove" : "wishlistAdd")}
                sx={(theme) => ({
                  border: 1,
                  borderColor: "divider",
                  flexShrink: 0,
                  color: saved.has(artwork.id) ? theme.tokens.action : "text.primary",
                })}
              >
                {saved.has(artwork.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
            </Stack>
            {artwork.year ? (
              <Typography color="text.secondary">{artwork.year}</Typography>
            ) : null}
            {(artwork.collections ?? []).length > 0 && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                {(artwork.collections ?? []).map((c) => (
                  <Chip
                    key={c.id}
                    size="small"
                    variant="outlined"
                    clickable
                    component={NextLink}
                    href={`${root}/zbirka/${c.slug}`}
                    label={localized(c.title, locale)}
                  />
                ))}
              </Stack>
            )}
            <ArtworkSetLink artworkSlug={artwork.slug} />

            {variants === null ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : printable.length === 0 ? (
              // An honest dead end rather than a size that would print badly.
              <Alert severity="info" sx={{ mt: 2 }}>
                {t("noSizesForWork")}
              </Alert>
            ) : (
              <>
                <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {price ? formatMoney(price.amount, price.currency, locale) : "—"}
                  </Typography>
                  {price?.compareAt != null && price.compareAt > price.amount && (
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: 18, textDecoration: "line-through" }}
                    >
                      {formatMoney(price.compareAt, price.currency, locale)}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <LocalShippingOutlinedIcon color="success" sx={{ fontSize: 18 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("delivery")}
                  </Typography>
                </Stack>

                <Divider ref={optionsRef} sx={{ my: 3, scrollMarginTop: 16 }} />

                {/* The editor's numbered steps. The counter lives on this
                    container, so a step that is not asked (the frame, for
                    paper) drops out of the count instead of leaving a gap. */}
                <OptionDensityContext.Provider value={desktop}>
                  <Stack spacing={desktop ? 2.5 : 3} sx={{ counterReset: STEP_COUNTER }}>
                    <FinishPicker
                      variants={printableVariants}
                      axes={axes}
                      finish={finish}
                      locale={locale}
                      onChange={changeFinishTo}
                      t={te}
                    />
                    <SizePicker
                      groups={groups}
                      selectedId={selected?.variant.id ?? null}
                      currency={currency}
                      locale={locale}
                      axes={axes}
                      orientation={orientation}
                      onOrientationChange={() => undefined}
                      onSelect={(v) => select(v.id)}
                      canRotate={false}
                      t={te}
                    />
                  </Stack>
                </OptionDensityContext.Provider>

                <Button
                  ref={addRef}
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{ mt: 3, minHeight: 52, fontWeight: 700 }}
                  disabled={adding || !selected}
                  onClick={handleAdd}
                  startIcon={
                    adding ? <CircularProgress size={16} color="inherit" /> : undefined
                  }
                >
                  {t("addToCart")}
                </Button>

                {/* What the customer gets, matched to what is actually being
                    sold: a rolled paper print is not stretched on a frame and
                    is not ready to hang. */}
                <Box sx={{ mt: 2 }}>
                  <TrustStrip lines={trustPointKeys(material, mount).map((key) => te(key))} />
                </Box>

                {/* Somebody other than the shop, right where the decision is. */}
                <ReviewsMarquee locale={locale} />
              </>
            )}

            {/* Museums ask for a credit line, and public domain is a claim
                worth making out loud. */}
            {artwork.attribution ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 3, display: "block", lineHeight: 1.6 }}
              >
                {artwork.attribution}
              </Typography>
            ) : null}

            {artwork.licence === "PUBLIC_DOMAIN" ? (
              <Chip
                size="small"
                variant="outlined"
                label={t("publicDomain")}
                sx={{ mt: 1.5 }}
              />
            ) : null}
          </Grid>
        </Grid>

        {description ? (
          <Box component="section" sx={{ mt: { xs: 5, md: 7 }, maxWidth: "68ch" }}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              {t("aboutWork")}
            </Typography>
            <Typography color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
              {description}
            </Typography>
          </Box>
        ) : null}

        <Box sx={{ mt: { xs: 5, md: 7 } }}>
          <ArtworkRow
            title={`${t("moreByArtist")}: ${artwork.artist}`}
            artworks={byArtist}
            card={cardProps}
          />
          <ArtworkRow title={t("similarWorks")} artworks={similarOnly} card={cardProps} />
        </Box>

        <Link component={NextLink} href={root} sx={{ display: "inline-block", mb: 4 }}>
          ← {t("backToGallery")}
        </Link>
      </Container>

      <MobileContinueBar
        targetRef={optionsRef}
        actionRef={addRef}
        label={selected ? sizeLabel(selected.widthMm, selected.heightMm) : null}
        price={price ? formatMoney(price.amount, price.currency, locale) : null}
      />

      <QuickAddSheet
        artwork={quickAdd}
        onClose={() => setQuickAdd(null)}
        hrefOf={(a) => `${root}/${a.slug}`}
        onAdd={add}
        adding={adding}
        error={pageError}
      />
    </>
  );
}
